/**
 * aiProvider.js
 * -----------------------------------------------------------
 * Abstraction du fournisseur IA. Les composants React ne connaissent
 * PAS le fournisseur réel : ils appellent uniquement `sendToProvider`.
 *
 * Trois modes supportés :
 *
 *   1. config.provider === "mock"
 *      Réponses locales déterministes-mais-contextualisées, aucune
 *      requête réseau. Utilisé pendant tout le développement.
 *
 *   2. config.provider === "http"
 *      Envoie une requête POST vers `config.httpEndpoint`.
 *
 *      -- Payload envoyé au backend ---------------------------------
 *      Content-Type: application/json
 *      {
 *        "message": "Question tapée par l'utilisateur",
 *        // ⚠ `message` n'est PAS dupliqué dans `history` :
 *        //    le backend doit l'appendre après les éléments de `history`
 *        //    avant l'appel au LLM.
 *        "history": [
 *          { "role": "blue" | "user", "content": "…" },
 *          …    // échanges ANTÉRIEURS uniquement,
 *               // taille max = config.historyWindowForProvider
 *        ],
 *        "context": {
 *          "pageType":    "destination",   // optionnel
 *          "title":       "Rome",           // optionnel
 *          "destination": "Rome",           // optionnel
 *          "userStatus":  "guest"|"member"|"partner"|…,  // optionnel
 *          "locale":      "fr",             // optionnel
 *          "extra":       { … }             // libre, optionnel
 *        },
 *        "locale": "fr"                     // duplication top-level pour
 *                                           // faciliter la lecture backend
 *      }
 *
 *      -- Réponse attendue -------------------------------------------
 *      HTTP 200 (application/json)
 *      { "reply": "Texte que Blue affichera" }
 *
 *      Toute autre forme (réponse vide, HTTP != 2xx, JSON invalide,
 *      timeout, erreur réseau) est traitée par BlueAssistant et
 *      convertie en message d'erreur convivial.
 *
 *      IMPORTANT : la clé API du moteur (OPENAI_API_KEY, ANTHROPIC_API_KEY,
 *      …) reste EXCLUSIVEMENT côté serveur. Le frontend ne la voit jamais.
 *
 *   3. config.provider === (async function)
 *      Fonction personnalisée : reçoit { message, history, context, locale }
 *      et doit renvoyer une chaîne. Utile pour les tests unitaires ou
 *      un branchement client-side spécifique.
 * -----------------------------------------------------------
 */

export async function sendToProvider({ message, history, context, config }) {
  const locale = (context && context.locale) || config.defaultLocale || "fr";
  const provider = config.provider;

  if (typeof provider === "function") {
    return provider({ message, history, context, locale });
  }

  if (provider === "http") {
    if (!config.httpEndpoint) {
      throw new Error(
        "[Blue] provider='http' mais aucun httpEndpoint n'est configuré."
      );
    }
    const controller = new AbortController();
    const timeoutMs = config.httpTimeoutMs || 25000;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(config.httpEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(config.httpHeaders || {}),
        },
        body: JSON.stringify({ message, history, context, locale }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`[Blue] HTTP ${res.status}`);
      const data = await res.json();
      // Phase 13.c : on remonte aussi les sources (avec badges de confiance)
      // au parent pour affichage du bloc « Sources utilisées ». Rétro-compatible :
      // le parent accepte soit une chaîne, soit { reply, sources }.
      const reply = typeof data.reply === "string" ? data.reply : "";
      return { reply, sources: Array.isArray(data.sources) ? data.sources : [] };
    } finally {
      clearTimeout(timer);
    }
  }

  // Défaut : mock local.
  return mockReply({ message, history, context, locale });
}

/* ------------------------------------------------------------------ */
/* Provider MOCK — démonstration contextuelle                         */
/* ------------------------------------------------------------------ */

const INTENTS = [
  {
    id: "greeting",
    keywords: ["bonjour", "salut", "coucou", "hello", "bonsoir", "hey"],
    reply: (ctx) =>
      `Bonjour ! Je suis Blue, la mascotte et guide ${site(ctx)}. ` +
      `Dites-moi ce qui vous ferait plaisir : une idée de voyage, une ` +
      `destination précise, ou un coup de main pour naviguer sur le site ?`,
  },
  {
    id: "farewell",
    keywords: ["merci", "au revoir", "à bientôt", "bye", "adieu", "bonne journée"],
    reply: () =>
      "Avec plaisir. Je reste dans le coin — cliquez sur moi dès que vous " +
      "avez une nouvelle question.",
  },
  {
    id: "prepare-trip",
    keywords: ["préparer un voyage", "preparer un voyage", "préparer", "organiser"],
    reply: (ctx) =>
      `Très bien, voyons ça ensemble. ${ctxLine(ctx)}Quelques questions ` +
      `pour cadrer : quelle période, combien de voyageurs, quel budget ` +
      `approximatif et quel type d'ambiance (mer, montagne, ville, croisière) ?`,
  },
  {
    id: "destinations",
    keywords: ["destination", "découvrir", "voyage", "partir", "vacances", "séjour", "escapade", "où aller"],
    reply: (ctx) =>
      `Bonne question. ${ctxLine(ctx)}Je pourrai bientôt vous suggérer ` +
      `des destinations selon vos envies (mer, montagne, culture, ` +
      `croisière…). Quel type d'ambiance recherchez-vous ?`,
  },
  {
    id: "cruise",
    keywords: ["croisière", "croisieres", "bateau", "msc", "navire", "paquebot"],
    reply: (ctx) =>
      `Les croisières font partie des spécialités ${site(ctx)}. ` +
      `${titleLine(ctx)}Dites-moi la région qui vous attire (Méditerranée, ` +
      `Caraïbes, Fjords…) et je vous oriente.`,
  },
  {
    id: "deals",
    keywords: ["bons plans", "bon plan", "promo", "promotion", "offre", "réduction", "moins cher"],
    reply: (ctx) =>
      `Les bons plans ${site(ctx)} sont rassemblés dans une rubrique dédiée. ` +
      `Précisez-moi une période ou une destination et je vous oriente vers ` +
      `les offres les plus pertinentes.`,
  },
  {
    id: "flights",
    keywords: ["vol", "avion", "aéroport", "billet"],
    reply: (ctx) =>
      `Pour les vols, ${site(ctx)} centralisera vos options aériennes. ` +
      `Précisez-moi votre ville de départ et vos dates, et je pourrai ` +
      `orienter votre recherche.`,
  },
  {
    id: "hotel",
    keywords: ["hôtel", "hotel", "hébergement", "logement", "airbnb"],
    reply: (ctx) =>
      `Pour l'hébergement, je pourrai vous guider vers les établissements ` +
      `partenaires ${site(ctx)}. ${destLine(ctx)}`,
  },
  {
    id: "membership",
    keywords: ["adhésion", "adhesion", "abonnement", "membre", "adherer"],
    reply: (ctx) => {
      if (ctx.userStatus === "member") {
        return "Vous êtes déjà membre. Je peux vous rappeler vos avantages " +
               "en cours ou vous aider à profiter de vos privilèges du moment.";
      }
      return "L'adhésion ITERCATUS ouvre l'accès aux offres membres, à des " +
             "tarifs privilégiés et au suivi de vos voyages. Je peux vous " +
             "expliquer les niveaux d'adhésion en détail dès que vous le souhaitez.";
    },
  },
  {
    id: "account",
    keywords: ["compte", "connexion", "connecter", "inscription", "mot de passe"],
    reply: (ctx) => {
      if (ctx.userStatus === "member") {
        return "Vous êtes déjà connecté(e), très bien. Souhaitez-vous " +
               "accéder à vos favoris, à un devis ou à une réservation en cours ?";
      }
      if (ctx.userStatus === "partner") {
        return "Espace partenaire détecté. Je peux vous orienter vers vos " +
               "outils de gestion ou la documentation.";
      }
      return "Vous n'êtes pas encore connecté(e). Vous pouvez continuer à " +
             "explorer librement, ou créer un compte pour sauvegarder vos " +
             "recherches et bénéficier des offres membres.";
    },
  },
  {
    id: "help",
    keywords: ["aide", "help", "comment", "où trouver", "je cherche", "faire", "itercatus"],
    reply: (ctx) =>
      `Je suis là pour ça. ${ctxLine(ctx)}Reformulez votre besoin en une ` +
      `phrase (par exemple « je voudrais partir en octobre en Sicile pour ` +
      `une semaine ») et je vous oriente au mieux.`,
  },
  {
    id: "contact",
    keywords: ["contact", "téléphone", "email", "conseiller", "humain"],
    reply: () =>
      "Un conseiller humain reste joignable via la page Contact du site. " +
      "Je peux aussi vous aider à préparer votre demande pour gagner du temps.",
  },
];

function site(ctx) {
  return ctx?.locale === "en" ? "ITERCATUS" : "d'ITERCATUS";
}

function ctxLine(ctx) {
  const parts = [];
  if (ctx?.pageType) parts.push(`page « ${ctx.pageType} »`);
  if (ctx?.title) parts.push(`« ${ctx.title} »`);
  if (ctx?.destination && ctx?.destination !== ctx?.title)
    parts.push(`destination ${ctx.destination}`);
  return parts.length ? `Je vois que vous êtes sur ${parts.join(" – ")}. ` : "";
}

function titleLine(ctx) {
  return ctx?.title ? `Vous consultez actuellement « ${ctx.title} ». ` : "";
}

function destLine(ctx) {
  return ctx?.destination
    ? `Pour ${ctx.destination}, je vous ferai plusieurs propositions dès ` +
      `qu'un vrai moteur IA sera branché.`
    : "Précisez-moi la destination et je vous orienterai.";
}

function findIntent(text) {
  const low = text.toLowerCase();
  for (const intent of INTENTS) {
    if (intent.keywords.some((k) => low.includes(k))) return intent;
  }
  return null;
}

async function mockReply({ message, context }) {
  // Délai artificiel pour laisser respirer l'état "thinking".
  const delay = 650 + Math.random() * 800;
  await new Promise((r) => setTimeout(r, delay));

  const q = (message || "").trim();
  if (!q) return "";

  const ctx = context || {};
  const intent = findIntent(q);
  if (intent) return intent.reply(ctx);

  const echo = q.length > 140 ? q.slice(0, 140) + "…" : q;
  return (
    `Merci pour votre message. ${ctxLine(ctx)}Vous m'avez écrit : « ${echo} ». ` +
    `Pour l'instant je fonctionne en mode démonstration (sans véritable ` +
    `moteur IA). Dès qu'un fournisseur sera branché, je pourrai vous ` +
    `répondre en détail. En attendant, je peux vous orienter sur les ` +
    `destinations, les croisières, la préparation d'un voyage ou la ` +
    `navigation sur le site.`
  );
}
